import Finder from '../../../common/helpers/finder';
import { printGuideLog, summarizePathsForLog } from '../helpers/guideLogHelper';
import { MessageTypes } from '../types/MessageTypes';

class ClickCapture {
  private static readonly CLICK_DEBOUNCE_MS = 100;
  private static readonly DISPATCH_DOUBLE_WARN_MS = 500;
  private static instance: ClickCapture | null = null;
  private top = 0;
  private left = 0;
  private width = 0;
  private height = 0;
  private isDispatching = false;
  private clickHandler: (e: MouseEvent) => void = () => {};
  private clientX = 0;
  private clientY = 0;
  private lastDispatchTime: number | null = null;
  private lastHandledClickWallTimeMs: number | null = null;
  private eventHandlers: Map<string, (event: MouseEvent) => void> = new Map();
  private paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[] = [];
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private constructor() {
    this.messageListener = event => {
      if (event.data.type === MessageTypes.SHOW_CLICK_CAPTURE) {
        this.show(event.data.paths);
      }
      if (event.data.type === MessageTypes.HIDE_CLICK_CAPTURE) {
        this.hide();
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  public static getInstance(): ClickCapture {
    if (!ClickCapture.instance) {
      ClickCapture.instance = new ClickCapture();
    }
    return ClickCapture.instance;
  }

  public static init(clickHandler: (e: MouseEvent) => void): void {
    ClickCapture.getInstance().clickHandler = clickHandler;
  }

  public show(
    paths: {
      xpath: string;
      cssPath: string[];
      textXPath: string | null;
      shadowRoot?: boolean;
      iframe?: boolean;
    }[]
  ): void {
    this.removeEventListeners();
    const { mainPaths, frameElement, framePaths } = Finder.splitPathsByIframe(paths);
    if (frameElement) {
      this.hide();
      this.paths = paths;
      frameElement.contentWindow?.postMessage(
        {
          type: MessageTypes.SHOW_CLICK_CAPTURE,
          paths: framePaths
        },
        '*'
      );
      return;
    }
    const element = Finder.findElementByPaths(mainPaths);

    if (!element) {
      printGuideLog({
        message: '[ClickCapture] show:targetNotFound',
        data: { pathSummary: summarizePathsForLog(mainPaths) },
        level: 'warn'
      });
      this.hide();
      this.paths = paths;
      return;
    }

    const dimensions = element.getBoundingClientRect();
    this.top = dimensions.y - 10;
    this.left = dimensions.x - 10;
    this.width = dimensions.width + 20;
    this.height = dimensions.height + 20;
    this.addEventListeners();
    this.paths = paths;
  }

  public hide(): void {
    this.removeEventListeners();
    this.top = 0;
    this.left = 0;
    this.width = 0;
    this.height = 0;
    this.isDispatching = false;
    this.lastDispatchTime = null;
    this.lastHandledClickWallTimeMs = null;
    const { frameElement, framePaths } = Finder.splitPathsByIframe(this.paths);
    if (frameElement) {
      frameElement.contentWindow?.postMessage(
        {
          type: MessageTypes.HIDE_CLICK_CAPTURE,
          paths: framePaths
        },
        '*'
      );
    }
  }

  public dispatchClick(element: HTMLElement): void {
    const now = Date.now();

    printGuideLog({
      message: '[ClickCapture] dispatchClick called',
      data: {
        tagName: element.tagName,
        clientX: this.clientX,
        clientY: this.clientY,
        deltaMs: this.lastDispatchTime ? now - this.lastDispatchTime : null
      }
    });

    if (
      this.lastDispatchTime &&
      now - this.lastDispatchTime < ClickCapture.DISPATCH_DOUBLE_WARN_MS
    ) {
      printGuideLog({
        message: '[ClickCapture] Possible double dispatch detected',
        data: { msSinceLastDispatch: now - this.lastDispatchTime },
        level: 'warn'
      });
    }
    this.lastDispatchTime = now;

    if (this.isDispatching) {
      printGuideLog({
        message: '[ClickCapture] dispatchClick ignored because a dispatch is already in progress',
        level: 'warn'
      });
      return;
    }
    this.isDispatching = true;

    const pointerId = 1;
    const common = {
      bubbles: true,
      cancelable: true,
      composed: true,
      isPrimary: true,
      pointerId,
      pointerType: 'mouse',
      clientX: this.clientX,
      clientY: this.clientY
    };

    const downProps = { ...common, buttons: 1 };
    const upProps = { ...common, buttons: 0 };

    element.dispatchEvent(new PointerEvent('pointerdown', { ...downProps, pressure: 0.5 }));
    element.dispatchEvent(new MouseEvent('mousedown', downProps));
    element.dispatchEvent(new PointerEvent('pointerup', { ...upProps, pressure: 0 }));
    element.dispatchEvent(new MouseEvent('mouseup', upProps));
    element.dispatchEvent(new MouseEvent('click', upProps));
    element.focus();

    setTimeout(() => {
      this.isDispatching = false;
    }, 100);
  }

  public destroy(): void {
    this.hide();
    this.removeEventListeners();
    ClickCapture.instance = null;
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }

  private addEventListeners(): void {
    const createEventHandler =
      (isClick = false) =>
      (event: Event) => {
        if (
          !this.isDispatching &&
          (event as MouseEvent).clientX > this.left &&
          (event as MouseEvent).clientX < this.left + this.width &&
          (event as MouseEvent).clientY > this.top &&
          (event as MouseEvent).clientY < this.top + this.height &&
          (event as MouseEvent).isTrusted
        ) {
          event.stopPropagation();
          event.preventDefault();
          if (isClick) {
            const mouseEvent = event as MouseEvent;
            printGuideLog({
              message: '[ClickCapture] capture zone click',
              data: { eventType: event.type, timeStamp: mouseEvent.timeStamp }
            });
            this.handleClick(mouseEvent);
          }
        }
      };

    const eventTypes: (keyof DocumentEventMap)[] = [
      'click',
      'mousedown',
      'mouseup',
      'pointerdown',
      'pointerup'
    ];

    eventTypes.forEach(eventType => {
      const isClick = eventType === 'click';
      const handler = createEventHandler(isClick);
      this.eventHandlers.set(eventType, handler as (event: MouseEvent) => void);
      document.addEventListener(eventType, handler, { capture: true });
    });
  }

  private removeEventListeners(): void {
    this.eventHandlers.forEach((handler, eventType) => {
      document.removeEventListener(eventType as keyof DocumentEventMap, handler as EventListener, {
        capture: true
      });
    });
    this.eventHandlers.clear();
  }

  private handleClick = (e: MouseEvent) => {
    const now = Date.now();
    if (this.lastHandledClickWallTimeMs !== null) {
      const deltaMs = now - this.lastHandledClickWallTimeMs;
      if (deltaMs < ClickCapture.CLICK_DEBOUNCE_MS) {
        printGuideLog({
          message: '[ClickCapture] handleClick ignored (debounce)',
          data: { deltaMs }
        });
        return;
      }
    }
    this.lastHandledClickWallTimeMs = now;

    this.clientX = e.clientX;
    this.clientY = e.clientY;
    printGuideLog({
      message: '[ClickCapture] handleClick captured',
      data: {
        clientX: e.clientX,
        clientY: e.clientY,
        targetTag: (e.target as HTMLElement | null)?.tagName
      }
    });
    this.clickHandler(e);
  };
}

export default ClickCapture;
